import { holdingUnitLifecycleStatusValues } from '@crv/contracts/holding-units';
import type {
  HoldingUnitCapabilities,
  HoldingUnitContainerKind,
  HoldingUnitIdentityMode,
  HoldingUnitLifecycleStatus,
  HoldingUnitProductKind,
  HoldingUnitType,
} from '@crv/contracts/holding-units';
import {
  CreateTareProfileRequestSchema,
  UpdateTareProfileRequestSchema,
  tareProfileModeValues,
  tareProfileSchemaLimits,
  type CreateTareProfileRequest,
  type CreateTareProfileResponse,
  type TareProfileDto,
  type TareProfileMode,
  type TareProfileValidationResult,
  type UpdateTareProfileRequest,
} from '@crv/contracts/tare-profiles';
import {
  CloneUnitProfileRequestSchema,
  CreateUnitProfileRequestSchema,
  UpdateUnitProfileRequestSchema,
  unitProfileSchemaLimits,
  unitProfileWorkflowValues,
  type CloneUnitProfileRequest,
  type CreateUnitProfileRequest,
  type CreateUnitProfileResponse,
  type UnitProfileCategory,
  type UnitProfileDto,
  type UnitProfileFixedIdPolicy,
  type UnitProfileTemplateDto,
  type UnitProfileTemplateKey,
  type UnitProfileValidationResult,
  type UnitProfileWorkflow,
  type UpdateUnitProfileRequest,
} from '@crv/contracts/unit-profiles';

import {
  UNIT_PROFILE_CAPABILITY_NAMES,
  buildStarterUnitProfileDefaults,
  deriveUnitProfileCapabilityPolicy,
  getUnitProfileTemplateDefinition,
  previewUnitProfileLifecycleCapabilities,
  previewUnitProfileWorkflowEligibility,
  validateTareProfile,
  validateUnitProfileAdminCapabilityPolicy,
  validateUnitProfileAdminTareCompatibility,
  validateUnitProfileStructuralEdit,
  type StarterProfileDefaults,
  type StarterProfileDefaultsOptions,
  type StarterTareProfileDefinition,
  type StarterUnitProfileDefinition,
  type UnitProfileCapabilityName,
  type UnitProfileLifecycleCapabilityPreview,
  type UnitProfileTemplateDefinition,
  type UnitProfileWorkflowEligibilityPreview,
} from './preview.js';

export type UnitProfileAdminFormMode = 'create' | 'clone' | 'edit';
export type TareProfileAdminFormMode = 'create' | 'edit';
export type AdminValidationSeverity = 'error' | 'warning';
export type AdminValidationSource = 'admin' | 'contract' | 'core';

export interface UnitProfileAdminScope {
  companyId: string;
  storeId: string;
  locationId: string;
}

export interface UnitProfileAdminStructuralState {
  templateKey: UnitProfileTemplateKey | '';
  profileCategory: UnitProfileCategory | '';
  unitType: HoldingUnitType | '';
  identityMode: HoldingUnitIdentityMode | '';
  containerKind: HoldingUnitContainerKind | '';
  productKind: HoldingUnitProductKind | '';
  fixedIdPolicy: UnitProfileFixedIdPolicy | '';
}

export interface UnitProfileAdminFormState extends UnitProfileAdminStructuralState {
  mode: UnitProfileAdminFormMode;
  sourceUnitProfileId: string;
  code: string;
  displayName: string;
  description: string;
  tareProfileId: string;
  capabilityPolicy: Partial<HoldingUnitCapabilities>;
  isActive: boolean;
  displayOrder: string;
}

export interface TareProfileAdminFormState {
  mode: TareProfileAdminFormMode;
  code: string;
  displayName: string;
  description: string;
  tareMode: TareProfileMode;
  defaultTareLb: string;
  allowOperatorOverride: boolean;
  requireOperatorConfirmation: boolean;
  isActive: boolean;
  displayOrder: string;
}

export interface AdminValidationIssue {
  code: string;
  field?: string;
  message: string;
  severity: AdminValidationSeverity;
  source: AdminValidationSource;
}

export interface AdminValidationResult {
  isValid: boolean;
  issues: AdminValidationIssue[];
  fieldErrors: Record<string, string[]>;
}

export interface UnitProfileAdminFormValidationInput {
  form: UnitProfileAdminFormState;
  existingProfile?: UnitProfileDto | null;
  sourceProfile?: UnitProfileDto | null;
  tareProfiles?: TareProfileDto[] | null;
}

export interface TareProfileAdminFormValidationInput {
  form: TareProfileAdminFormState;
  existingProfile?: TareProfileDto | null;
}

export interface AdminLabelDescriptor {
  label: string;
  description: string;
}

export interface UnitProfileCapabilityPreviewRow extends AdminLabelDescriptor {
  capability: UnitProfileCapabilityName;
  templateAllowed: boolean;
  requested: boolean;
  ceilingViolation: boolean;
  effectiveByLifecycleStatus: Record<HoldingUnitLifecycleStatus, boolean> | null;
}

export interface UnitProfileWorkflowPreviewRow extends AdminLabelDescriptor {
  workflow: UnitProfileWorkflow;
  isActive: boolean;
  isEligible: boolean;
  validation: UnitProfileWorkflowEligibilityPreview['validation'];
}

export interface UnitProfileDraftPreview {
  validation: AdminValidationResult;
  selectedTareProfile: TareProfileDto | null;
  lifecycleCapabilities: UnitProfileLifecycleCapabilityPreview;
  capabilityRows: UnitProfileCapabilityPreviewRow[];
  workflows: UnitProfileWorkflowPreviewRow[];
}

export interface StarterDefaultProfileStatus<TDefinition, TExisting> {
  code: string;
  definition: TDefinition;
  existing: TExisting | null;
  isMissing: boolean;
}

export interface StarterDefaultDetection {
  defaults: StarterProfileDefaults;
  tareProfiles: StarterDefaultProfileStatus<StarterTareProfileDefinition, TareProfileDto>[];
  unitProfiles: StarterDefaultProfileStatus<StarterUnitProfileDefinition, UnitProfileDto>[];
  missingTareProfiles: StarterTareProfileDefinition[];
  missingUnitProfiles: StarterUnitProfileDefinition[];
  hasMissingDefaults: boolean;
}

export interface StarterDefaultBackfillClient {
  createTareProfile(request: CreateTareProfileRequest): Promise<CreateTareProfileResponse>;
  createUnitProfile(request: CreateUnitProfileRequest): Promise<CreateUnitProfileResponse>;
}

export type StarterDefaultBackfillRecordStatus =
  | 'skipped_existing'
  | 'created'
  | 'failed'
  | 'blocked_missing_tare';

export interface StarterDefaultBackfillRecord {
  kind: 'tare' | 'unit';
  code: string;
  status: StarterDefaultBackfillRecordStatus;
  item?: TareProfileDto | UnitProfileDto;
  errorMessage?: string;
  tareProfileCode?: string;
}

export interface StarterDefaultBackfillResult {
  detection: StarterDefaultDetection;
  records: StarterDefaultBackfillRecord[];
  createdTareProfiles: TareProfileDto[];
  createdUnitProfiles: UnitProfileDto[];
  hasFailures: boolean;
}

export const UNIT_PROFILE_ADMIN_IMMUTABLE_FIELDS = [
  'code',
  'templateKey',
  'profileCategory',
  'unitType',
  'identityMode',
  'containerKind',
  'productKind',
  'fixedIdPolicy',
] as const;

export const UNIT_PROFILE_ADMIN_CONFIGURABLE_FIELDS = [
  'displayName',
  'description',
  'tareProfileId',
  'capabilityPolicy',
  'isActive',
  'displayOrder',
] as const;

export const TARE_PROFILE_ADMIN_IMMUTABLE_FIELDS = ['code'] as const;

export const TARE_PROFILE_ADMIN_CONFIGURABLE_FIELDS = [
  'displayName',
  'description',
  'mode',
  'defaultTareLb',
  'allowOperatorOverride',
  'requireOperatorConfirmation',
  'isActive',
  'displayOrder',
] as const;

const EMPTY_UNIT_PROFILE_STRUCTURAL_STATE: UnitProfileAdminStructuralState = {
  templateKey: '',
  profileCategory: '',
  unitType: '',
  identityMode: '',
  containerKind: '',
  productKind: '',
  fixedIdPolicy: '',
};

const UNIT_PROFILE_WORKFLOW_LABELS = {
  fast_weigh_create: {
    label: 'Fast Weigh',
    description: 'Eligible for creating source units from Fast Weigh intake.',
  },
  fixed_id_asset_start: {
    label: 'Fixed-ID Start',
    description: 'Eligible for starting a fixed-ID asset cycle.',
  },
  build_product_input: {
    label: 'Build Product Input',
    description: 'Eligible as an input unit for build-product workflows.',
  },
  build_product_output: {
    label: 'Build Product Output',
    description: 'Eligible as a product output from build-product workflows.',
  },
  build_product_leftover: {
    label: 'Build Product Leftover',
    description: 'Eligible as leftover output from build-product workflows.',
  },
  inbound_receipt_product: {
    label: 'Inbound Product',
    description: 'Eligible as product inventory posted from inbound receipts.',
  },
  inbound_receipt_unit: {
    label: 'Inbound Unit',
    description: 'Eligible as a source or product unit posted from inbound receipts.',
  },
  transfer_source: {
    label: 'Yard Transfer',
    description: 'Eligible for transfer out of its current yard position.',
  },
  shipment_candidate: {
    label: 'Shipment Candidate',
    description: 'Eligible for shipment and truck-loading selection.',
  },
} as const satisfies Record<UnitProfileWorkflow, AdminLabelDescriptor>;

const UNIT_PROFILE_CAPABILITY_LABELS = {
  canReceiveIntake: {
    label: 'Receive Intake',
    description: 'Allows new intake weight to be received into this profile.',
  },
  canReceiveTransfer: {
    label: 'Receive Transfer',
    description: 'Allows weight transferred from another holding unit.',
  },
  canTransferOut: {
    label: 'Transfer Out',
    description: 'Allows weight to be transferred out to another holding unit.',
  },
  canAdjust: {
    label: 'Adjust Weight',
    description: 'Allows manual correction or adjustment of held weight.',
  },
  canReady: {
    label: 'Mark Ready',
    description: 'Allows open units to be marked ready for downstream work.',
  },
  canShip: {
    label: 'Ship',
    description: 'Allows ready units to be selected for shipment.',
  },
  canRetire: {
    label: 'Retire',
    description: 'Allows operational retirement when the unit is no longer usable.',
  },
  canBuildProduct: {
    label: 'Build Product',
    description: 'Allows use as an input in build-product workflows.',
  },
  canHoldLeftover: {
    label: 'Hold Leftover',
    description: 'Allows leftover material to remain held after product build.',
  },
  canReopen: {
    label: 'Reopen',
    description: 'Allows ready units to return to open status when permitted.',
  },
  canDisassemble: {
    label: 'Disassemble',
    description: 'Allows product units to be broken back into held material.',
  },
} as const satisfies Record<UnitProfileCapabilityName, AdminLabelDescriptor>;

const TARE_PROFILE_MODE_LABELS = {
  none: {
    label: 'No Tare',
    description: 'No default or operator tare is applied.',
  },
  fixed_default: {
    label: 'Fixed Default',
    description: 'A configured default tare is suggested for each unit.',
  },
  manual_required: {
    label: 'Manual Required',
    description: 'The operator must enter tare before net weight can be resolved.',
  },
  fixed_asset_master: {
    label: 'Fixed Asset Master',
    description: 'Tare is resolved from the fixed-ID asset master record.',
  },
} as const satisfies Record<TareProfileMode, AdminLabelDescriptor>;

export function buildCreateUnitProfileFormState(input?: {
  template?: UnitProfileTemplateDto | UnitProfileTemplateDefinition | null;
  displayOrder?: number;
}): UnitProfileAdminFormState {
  const template = input?.template ?? null;
  return {
    mode: 'create',
    sourceUnitProfileId: '',
    code: '',
    displayName: template?.displayName ?? '',
    description: '',
    ...structuralStateFromTemplate(template),
    tareProfileId: '',
    capabilityPolicy: template ? { ...template.defaultCapabilityPolicy } : {},
    isActive: true,
    displayOrder: String(input?.displayOrder ?? 0),
  };
}

export function buildCloneUnitProfileFormState(
  source: UnitProfileDto,
  input?: { displayOrder?: number },
): UnitProfileAdminFormState {
  return {
    mode: 'clone',
    sourceUnitProfileId: source.id,
    code: '',
    displayName: '',
    description: source.description ?? '',
    ...structuralStateFromProfile(source),
    tareProfileId: source.tareProfileId ?? '',
    capabilityPolicy: { ...source.capabilityPolicy },
    isActive: true,
    displayOrder: String(input?.displayOrder ?? source.displayOrder + 1),
  };
}

export function buildEditUnitProfileFormState(profile: UnitProfileDto): UnitProfileAdminFormState {
  return {
    mode: 'edit',
    sourceUnitProfileId: '',
    code: profile.code,
    displayName: profile.displayName,
    description: profile.description ?? '',
    ...structuralStateFromProfile(profile),
    tareProfileId: profile.tareProfileId ?? '',
    capabilityPolicy: { ...profile.capabilityPolicy },
    isActive: profile.isActive,
    displayOrder: String(profile.displayOrder),
  };
}

export function applyUnitProfileTemplateToFormState(
  form: UnitProfileAdminFormState,
  template: UnitProfileTemplateDto | UnitProfileTemplateDefinition,
): UnitProfileAdminFormState {
  return {
    ...form,
    mode: 'create',
    sourceUnitProfileId: '',
    displayName: form.displayName.trim() ? form.displayName : template.displayName,
    ...structuralStateFromTemplate(template),
    capabilityPolicy: { ...template.defaultCapabilityPolicy },
  };
}

export function applyUnitProfileCloneSourceToFormState(
  form: UnitProfileAdminFormState,
  source: UnitProfileDto,
): UnitProfileAdminFormState {
  return {
    ...form,
    mode: 'clone',
    sourceUnitProfileId: source.id,
    description: source.description ?? '',
    ...structuralStateFromProfile(source),
    tareProfileId: source.tareProfileId ?? '',
    capabilityPolicy: { ...source.capabilityPolicy },
    displayOrder: String(source.displayOrder + 1),
  };
}

export function buildCreateTareProfileFormState(input?: {
  displayOrder?: number;
}): TareProfileAdminFormState {
  return {
    mode: 'create',
    code: '',
    displayName: '',
    description: '',
    tareMode: 'none',
    defaultTareLb: '',
    allowOperatorOverride: false,
    requireOperatorConfirmation: false,
    isActive: true,
    displayOrder: String(input?.displayOrder ?? 0),
  };
}

export function buildEditTareProfileFormState(profile: TareProfileDto): TareProfileAdminFormState {
  return {
    mode: 'edit',
    code: profile.code,
    displayName: profile.displayName,
    description: profile.description ?? '',
    tareMode: profile.mode,
    defaultTareLb: profile.defaultTareLb == null ? '' : String(profile.defaultTareLb),
    allowOperatorOverride: profile.allowOperatorOverride,
    requireOperatorConfirmation: profile.requireOperatorConfirmation,
    isActive: profile.isActive,
    displayOrder: String(profile.displayOrder),
  };
}

export function buildCreateUnitProfileRequestFromFormState(
  scope: UnitProfileAdminScope,
  form: UnitProfileAdminFormState,
): CreateUnitProfileRequest {
  return CreateUnitProfileRequestSchema.parse({
    ...scope,
    code: form.code.trim(),
    displayName: form.displayName.trim(),
    description: normalizeDescription(form.description),
    templateKey: form.templateKey,
    tareProfileId: normalizeOptionalId(form.tareProfileId),
    capabilityPolicy: { ...form.capabilityPolicy },
    isActive: form.isActive,
    displayOrder: parseDisplayOrderForRequest(form.displayOrder),
  });
}

export function buildCloneUnitProfileRequestFromFormState(
  companyId: string,
  form: UnitProfileAdminFormState,
): CloneUnitProfileRequest {
  return CloneUnitProfileRequestSchema.parse({
    companyId,
    sourceUnitProfileId: form.sourceUnitProfileId,
    code: form.code.trim(),
    displayName: form.displayName.trim(),
    description: normalizeDescription(form.description),
    tareProfileId: normalizeOptionalId(form.tareProfileId),
    capabilityPolicy: { ...form.capabilityPolicy },
    isActive: form.isActive,
    displayOrder: parseDisplayOrderForRequest(form.displayOrder),
  });
}

export function buildUpdateUnitProfileRequestFromFormState(
  companyId: string,
  unitProfileId: string,
  form: UnitProfileAdminFormState,
): UpdateUnitProfileRequest {
  return UpdateUnitProfileRequestSchema.parse({
    companyId,
    unitProfileId,
    displayName: form.displayName.trim(),
    description: normalizeDescription(form.description),
    tareProfileId: normalizeOptionalId(form.tareProfileId),
    capabilityPolicy: { ...form.capabilityPolicy },
    isActive: form.isActive,
    displayOrder: parseDisplayOrderForRequest(form.displayOrder),
  });
}

export function buildCreateTareProfileRequestFromFormState(
  scope: UnitProfileAdminScope,
  form: TareProfileAdminFormState,
): CreateTareProfileRequest {
  return CreateTareProfileRequestSchema.parse({
    ...scope,
    code: form.code.trim(),
    displayName: form.displayName.trim(),
    description: normalizeDescription(form.description),
    mode: form.tareMode,
    defaultTareLb: parseDefaultTareForRequest(form),
    allowOperatorOverride: form.allowOperatorOverride,
    requireOperatorConfirmation: form.requireOperatorConfirmation,
    isActive: form.isActive,
    displayOrder: parseDisplayOrderForRequest(form.displayOrder),
  });
}

export function buildUpdateTareProfileRequestFromFormState(
  companyId: string,
  tareProfileId: string,
  form: TareProfileAdminFormState,
): UpdateTareProfileRequest {
  return UpdateTareProfileRequestSchema.parse({
    companyId,
    tareProfileId,
    displayName: form.displayName.trim(),
    description: normalizeDescription(form.description),
    mode: form.tareMode,
    defaultTareLb: parseDefaultTareForRequest(form),
    allowOperatorOverride: form.allowOperatorOverride,
    requireOperatorConfirmation: form.requireOperatorConfirmation,
    isActive: form.isActive,
    displayOrder: parseDisplayOrderForRequest(form.displayOrder),
  });
}

export function validateUnitProfileAdminFormState(
  input: UnitProfileAdminFormValidationInput,
): AdminValidationResult {
  const issues: AdminValidationIssue[] = [];
  const { form } = input;

  if (form.mode !== 'edit') {
    addRequiredTextIssue(issues, 'code', form.code, 'Code is required.');
  }
  addRequiredTextIssue(issues, 'displayName', form.displayName, 'Display name is required.');
  addTextLimitIssue(
    issues,
    'code',
    form.code,
    unitProfileSchemaLimits.maxCodeLength,
    'Code is too long.',
  );
  addTextLimitIssue(
    issues,
    'displayName',
    form.displayName,
    unitProfileSchemaLimits.maxDisplayNameLength,
    'Display name is too long.',
  );
  addTextLimitIssue(
    issues,
    'description',
    form.description,
    unitProfileSchemaLimits.maxDescriptionLength,
    'Description is too long.',
  );
  addDisplayOrderIssue(issues, form.displayOrder);

  if (form.mode === 'create' && !form.templateKey) {
    issues.push(adminIssue('required', 'templateKey', 'Select a unit profile template.'));
  }
  if (form.mode === 'clone' && !form.sourceUnitProfileId) {
    issues.push(adminIssue('required', 'sourceUnitProfileId', 'Select a source unit profile.'));
  }

  const templateKey = resolveFormTemplateKey(form, input.existingProfile, input.sourceProfile);
  const template = templateKey ? getUnitProfileTemplateDefinition(templateKey) : null;
  if (templateKey && !template) {
    issues.push(
      adminIssue(
        'invalid_template_key',
        'templateKey',
        'Unit profile templateKey is not recognized.',
        'core',
      ),
    );
  }

  if (form.mode === 'edit' && input.existingProfile) {
    addUnitProfileValidationResult(
      issues,
      validateUnitProfileStructuralEdit(
        structuralComparableFromProfile(input.existingProfile),
        structuralComparableFromForm(form, input.existingProfile),
      ),
    );
  }

  if (templateKey) {
    addUnitProfileValidationResult(
      issues,
      validateUnitProfileAdminCapabilityPolicy({
        templateKey,
        capabilityPolicy: form.capabilityPolicy,
      }),
    );
  }

  const selectedTare = resolveSelectedTareProfile(form, input.tareProfiles ?? null);
  if (form.tareProfileId && input.tareProfiles && !selectedTare) {
    issues.push(
      adminIssue(
        'tare_profile_unavailable',
        'tareProfileId',
        'Selected tare profile is not available for this location.',
      ),
    );
  }

  if (selectedTare) {
    addTareProfileValidationResult(
      issues,
      validateTareProfile({
        mode: selectedTare.mode,
        defaultTareLb: selectedTare.defaultTareLb,
      }),
    );
  }

  if (templateKey) {
    addUnitProfileValidationResult(
      issues,
      validateUnitProfileAdminTareCompatibility({
        templateKey,
        tareProfile: selectedTare
          ? { mode: selectedTare.mode, defaultTareLb: selectedTare.defaultTareLb }
          : null,
      }),
    );
  }

  if (!form.isActive) {
    issues.push(
      adminIssue(
        'inactive_profile_hidden_from_operations',
        'isActive',
        'Inactive profiles remain visible in admin but are hidden from new operational unit creation.',
        'admin',
        'warning',
      ),
    );
  }

  return toAdminValidationResult(issues);
}

export function validateTareProfileAdminFormState(
  input: TareProfileAdminFormValidationInput,
): AdminValidationResult {
  const issues: AdminValidationIssue[] = [];
  const { form } = input;

  if (form.mode === 'create') {
    addRequiredTextIssue(issues, 'code', form.code, 'Code is required.');
  }
  addRequiredTextIssue(issues, 'displayName', form.displayName, 'Display name is required.');
  addTextLimitIssue(
    issues,
    'code',
    form.code,
    tareProfileSchemaLimits.maxCodeLength,
    'Code is too long.',
  );
  addTextLimitIssue(
    issues,
    'displayName',
    form.displayName,
    tareProfileSchemaLimits.maxDisplayNameLength,
    'Display name is too long.',
  );
  addTextLimitIssue(
    issues,
    'description',
    form.description,
    tareProfileSchemaLimits.maxDescriptionLength,
    'Description is too long.',
  );
  addDisplayOrderIssue(issues, form.displayOrder);

  if (form.mode === 'edit' && input.existingProfile && form.code !== input.existingProfile.code) {
    issues.push(
      adminIssue(
        'structural_edit_not_allowed',
        'code',
        'code is create-only and cannot be changed after creation.',
        'core',
      ),
    );
  }

  if (!tareProfileModeValues.includes(form.tareMode)) {
    issues.push(
      adminIssue('invalid_tare_profile_mode', 'mode', 'Tare profile mode is not recognized.'),
    );
  }

  const parsedDefaultTare = parseDefaultTareForValidation(form, issues);
  addTareProfileValidationResult(
    issues,
    validateTareProfile({
      mode: form.tareMode,
      defaultTareLb: parsedDefaultTare,
    }),
  );

  if (!form.isActive) {
    issues.push(
      adminIssue(
        'inactive_profile_hidden_from_operations',
        'isActive',
        'Inactive tare profiles remain visible in admin but should not be selected for new unit profiles.',
        'admin',
        'warning',
      ),
    );
  }

  return toAdminValidationResult(issues);
}

export function validateUnitProfileAdminPayloadFields(input: {
  operation: UnitProfileAdminFormMode;
  payload: Record<string, unknown>;
}): AdminValidationResult {
  const allowedFields =
    input.operation === 'edit'
      ? new Set(['companyId', 'unitProfileId', ...UNIT_PROFILE_ADMIN_CONFIGURABLE_FIELDS])
      : input.operation === 'clone'
        ? new Set([
            'companyId',
            'sourceUnitProfileId',
            'code',
            ...UNIT_PROFILE_ADMIN_CONFIGURABLE_FIELDS,
          ])
        : new Set([
            'companyId',
            'storeId',
            'locationId',
            'code',
            'templateKey',
            ...UNIT_PROFILE_ADMIN_CONFIGURABLE_FIELDS,
          ]);

  return validatePayloadFields(input.payload, allowedFields, UNIT_PROFILE_ADMIN_IMMUTABLE_FIELDS);
}

export function validateTareProfileAdminPayloadFields(input: {
  operation: TareProfileAdminFormMode;
  payload: Record<string, unknown>;
}): AdminValidationResult {
  const allowedFields =
    input.operation === 'edit'
      ? new Set(['companyId', 'tareProfileId', ...TARE_PROFILE_ADMIN_CONFIGURABLE_FIELDS])
      : new Set([
          'companyId',
          'storeId',
          'locationId',
          'code',
          ...TARE_PROFILE_ADMIN_CONFIGURABLE_FIELDS,
        ]);

  return validatePayloadFields(input.payload, allowedFields, TARE_PROFILE_ADMIN_IMMUTABLE_FIELDS);
}

export function getUnitProfileWorkflowLabel(workflow: UnitProfileWorkflow): AdminLabelDescriptor {
  return UNIT_PROFILE_WORKFLOW_LABELS[workflow];
}

export function listUnitProfileWorkflowLabels(): Array<
  AdminLabelDescriptor & { workflow: UnitProfileWorkflow }
> {
  return unitProfileWorkflowValues.map((workflow) => ({
    workflow,
    ...UNIT_PROFILE_WORKFLOW_LABELS[workflow],
  }));
}

export function getUnitProfileCapabilityLabel(
  capability: UnitProfileCapabilityName,
): AdminLabelDescriptor {
  return UNIT_PROFILE_CAPABILITY_LABELS[capability];
}

export function listUnitProfileCapabilityLabels(): Array<
  AdminLabelDescriptor & { capability: UnitProfileCapabilityName }
> {
  return UNIT_PROFILE_CAPABILITY_NAMES.map((capability) => ({
    capability,
    ...UNIT_PROFILE_CAPABILITY_LABELS[capability],
  }));
}

export function getTareProfileModeLabel(mode: TareProfileMode): AdminLabelDescriptor {
  return TARE_PROFILE_MODE_LABELS[mode];
}

export function listTareProfileModeLabels(): Array<
  AdminLabelDescriptor & { mode: TareProfileMode }
> {
  return tareProfileModeValues.map((mode) => ({
    mode,
    ...TARE_PROFILE_MODE_LABELS[mode],
  }));
}

export function buildUnitProfileCapabilityPreviewRows(input: {
  templateKey: string;
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null;
}): UnitProfileCapabilityPreviewRow[] {
  const template = getUnitProfileTemplateDefinition(input.templateKey);
  if (!template) {
    return [];
  }

  const lifecyclePreview = previewUnitProfileLifecycleCapabilities(input);
  const derivedPolicy = deriveCapabilityPolicyForPreview(template, input.capabilityPolicy);

  return UNIT_PROFILE_CAPABILITY_NAMES.map((capability) => {
    const label = getUnitProfileCapabilityLabel(capability);
    const requested = derivedPolicy[capability];
    const templateAllowed = template.maxCapabilities[capability];
    return {
      capability,
      ...label,
      templateAllowed,
      requested,
      ceilingViolation: requested && !templateAllowed,
      effectiveByLifecycleStatus: lifecyclePreview.effectiveCapabilitiesByLifecycleStatus
        ? lifecycleBooleans(lifecyclePreview.effectiveCapabilitiesByLifecycleStatus, capability)
        : null,
    };
  });
}

export function buildUnitProfileDraftPreview(input: {
  form: UnitProfileAdminFormState;
  existingProfile?: UnitProfileDto | null;
  sourceProfile?: UnitProfileDto | null;
  tareProfiles?: TareProfileDto[] | null;
  includeInactive?: boolean;
}): UnitProfileDraftPreview {
  const validation = validateUnitProfileAdminFormState(input);
  const templateKey = resolveFormTemplateKey(
    input.form,
    input.existingProfile,
    input.sourceProfile,
  );
  const selectedTareProfile = resolveSelectedTareProfile(input.form, input.tareProfiles ?? null);
  const lifecycleCapabilities = previewUnitProfileLifecycleCapabilities({
    templateKey,
    capabilityPolicy: input.form.capabilityPolicy,
  });
  const capabilityRows = templateKey
    ? buildUnitProfileCapabilityPreviewRows({
        templateKey,
        capabilityPolicy: input.form.capabilityPolicy,
      })
    : [];
  const workflows = unitProfileWorkflowValues.map((workflow) =>
    buildWorkflowPreviewRow({
      workflow,
      templateKey,
      form: input.form,
      selectedTareProfile,
      includeInactive: input.includeInactive,
    }),
  );

  return {
    validation,
    selectedTareProfile,
    lifecycleCapabilities,
    capabilityRows,
    workflows,
  };
}

export function detectMissingStarterDefaults(input: {
  currentTareProfiles: TareProfileDto[];
  currentUnitProfiles: UnitProfileDto[];
  defaultsOptions?: StarterProfileDefaultsOptions;
}): StarterDefaultDetection {
  const defaults = buildStarterUnitProfileDefaults(input.defaultsOptions);
  return detectMissingStarterDefaultsFromDefinitions({
    currentTareProfiles: input.currentTareProfiles,
    currentUnitProfiles: input.currentUnitProfiles,
    defaults,
  });
}

export function detectMissingStarterDefaultsFromDefinitions(input: {
  currentTareProfiles: TareProfileDto[];
  currentUnitProfiles: UnitProfileDto[];
  defaults: StarterProfileDefaults;
}): StarterDefaultDetection {
  const tareByCode = new Map(input.currentTareProfiles.map((profile) => [profile.code, profile]));
  const unitByCode = new Map(input.currentUnitProfiles.map((profile) => [profile.code, profile]));
  const tareProfiles = input.defaults.tareProfiles.map((definition) => {
    const existing = tareByCode.get(definition.code) ?? null;
    return {
      code: definition.code,
      definition,
      existing,
      isMissing: !existing,
    };
  });
  const unitProfiles = input.defaults.unitProfiles.map((definition) => {
    const existing = unitByCode.get(definition.code) ?? null;
    return {
      code: definition.code,
      definition,
      existing,
      isMissing: !existing,
    };
  });
  const missingTareProfiles = tareProfiles
    .filter((profile) => profile.isMissing)
    .map((profile) => profile.definition);
  const missingUnitProfiles = unitProfiles
    .filter((profile) => profile.isMissing)
    .map((profile) => profile.definition);

  return {
    defaults: input.defaults,
    tareProfiles,
    unitProfiles,
    missingTareProfiles,
    missingUnitProfiles,
    hasMissingDefaults: missingTareProfiles.length > 0 || missingUnitProfiles.length > 0,
  };
}

export async function backfillMissingStarterDefaults(input: {
  scope: UnitProfileAdminScope;
  currentTareProfiles: TareProfileDto[];
  currentUnitProfiles: UnitProfileDto[];
  client: StarterDefaultBackfillClient;
  defaultsOptions?: StarterProfileDefaultsOptions;
}): Promise<StarterDefaultBackfillResult> {
  const detection = detectMissingStarterDefaults({
    currentTareProfiles: input.currentTareProfiles,
    currentUnitProfiles: input.currentUnitProfiles,
    defaultsOptions: input.defaultsOptions,
  });
  const records: StarterDefaultBackfillRecord[] = [];
  const createdTareProfiles: TareProfileDto[] = [];
  const createdUnitProfiles: UnitProfileDto[] = [];
  const tareByCode = new Map(input.currentTareProfiles.map((profile) => [profile.code, profile]));

  for (const status of detection.tareProfiles) {
    if (!status.isMissing && status.existing) {
      records.push({
        kind: 'tare',
        code: status.code,
        status: 'skipped_existing',
        item: status.existing,
      });
      continue;
    }

    try {
      const response = await input.client.createTareProfile({
        ...input.scope,
        ...status.definition,
      });
      createdTareProfiles.push(response.item);
      tareByCode.set(status.code, response.item);
      records.push({
        kind: 'tare',
        code: status.code,
        status: 'created',
        item: response.item,
      });
    } catch (error) {
      records.push({
        kind: 'tare',
        code: status.code,
        status: 'failed',
        errorMessage: getUnknownErrorMessage(error),
      });
    }
  }

  for (const status of detection.unitProfiles) {
    if (!status.isMissing && status.existing) {
      records.push({
        kind: 'unit',
        code: status.code,
        status: 'skipped_existing',
        item: status.existing,
        tareProfileCode: status.definition.tareProfileCode,
      });
      continue;
    }

    const tareProfile = tareByCode.get(status.definition.tareProfileCode);
    if (!tareProfile) {
      records.push({
        kind: 'unit',
        code: status.code,
        status: 'blocked_missing_tare',
        tareProfileCode: status.definition.tareProfileCode,
        errorMessage: `Missing starter tare profile ${status.definition.tareProfileCode}.`,
      });
      continue;
    }

    try {
      const response = await input.client.createUnitProfile({
        ...input.scope,
        code: status.definition.code,
        displayName: status.definition.displayName,
        description: status.definition.description,
        templateKey: status.definition.templateKey,
        tareProfileId: tareProfile.id,
        capabilityPolicy: status.definition.capabilityPolicy,
        isActive: status.definition.isActive,
        displayOrder: status.definition.displayOrder,
      });
      createdUnitProfiles.push(response.item);
      records.push({
        kind: 'unit',
        code: status.code,
        status: 'created',
        item: response.item,
        tareProfileCode: status.definition.tareProfileCode,
      });
    } catch (error) {
      records.push({
        kind: 'unit',
        code: status.code,
        status: 'failed',
        tareProfileCode: status.definition.tareProfileCode,
        errorMessage: getUnknownErrorMessage(error),
      });
    }
  }

  return {
    detection,
    records,
    createdTareProfiles,
    createdUnitProfiles,
    hasFailures: records.some(
      (record) => record.status === 'failed' || record.status === 'blocked_missing_tare',
    ),
  };
}

function structuralStateFromTemplate(
  template: UnitProfileTemplateDto | UnitProfileTemplateDefinition | null,
): UnitProfileAdminStructuralState {
  if (!template) {
    return { ...EMPTY_UNIT_PROFILE_STRUCTURAL_STATE };
  }
  return {
    templateKey: template.templateKey,
    profileCategory: template.profileCategory,
    unitType: template.unitType,
    identityMode: template.identityMode,
    containerKind: template.containerKind,
    productKind: template.productKind,
    fixedIdPolicy: template.fixedIdPolicy,
  };
}

function structuralStateFromProfile(profile: UnitProfileDto): UnitProfileAdminStructuralState {
  return {
    templateKey: profile.templateKey,
    profileCategory: profile.profileCategory,
    unitType: profile.unitType,
    identityMode: profile.identityMode,
    containerKind: profile.containerKind,
    productKind: profile.productKind,
    fixedIdPolicy: profile.fixedIdPolicy,
  };
}

function structuralComparableFromProfile(
  profile: UnitProfileDto,
): Record<(typeof UNIT_PROFILE_ADMIN_IMMUTABLE_FIELDS)[number], string> {
  return {
    code: profile.code,
    templateKey: profile.templateKey,
    profileCategory: profile.profileCategory,
    unitType: profile.unitType,
    identityMode: profile.identityMode,
    containerKind: profile.containerKind,
    productKind: profile.productKind,
    fixedIdPolicy: profile.fixedIdPolicy,
  };
}

function structuralComparableFromForm(
  form: UnitProfileAdminFormState,
  existing: UnitProfileDto,
): Record<(typeof UNIT_PROFILE_ADMIN_IMMUTABLE_FIELDS)[number], string> {
  return {
    code: form.code,
    templateKey: form.templateKey || existing.templateKey,
    profileCategory: form.profileCategory || existing.profileCategory,
    unitType: form.unitType || existing.unitType,
    identityMode: form.identityMode || existing.identityMode,
    containerKind: form.containerKind || existing.containerKind,
    productKind: form.productKind || existing.productKind,
    fixedIdPolicy: form.fixedIdPolicy || existing.fixedIdPolicy,
  };
}

function resolveFormTemplateKey(
  form: UnitProfileAdminFormState,
  existingProfile?: UnitProfileDto | null,
  sourceProfile?: UnitProfileDto | null,
): string {
  if (form.mode === 'edit') {
    return existingProfile?.templateKey ?? form.templateKey;
  }
  if (form.mode === 'clone') {
    return sourceProfile?.templateKey ?? form.templateKey;
  }
  return form.templateKey;
}

function normalizeDescription(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeOptionalId(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseDisplayOrderForRequest(value: string): number {
  return parseNonNegativeInteger(value) ?? 0;
}

function parseNonNegativeInteger(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return 0;
  }
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function parseDefaultTareForRequest(form: TareProfileAdminFormState): number | null {
  if (form.tareMode !== 'fixed_default') {
    return null;
  }
  return parseNonNegativeNumber(form.defaultTareLb);
}

function parseDefaultTareForValidation(
  form: TareProfileAdminFormState,
  issues: AdminValidationIssue[],
): number | null {
  const trimmed = form.defaultTareLb.trim();
  if (form.tareMode !== 'fixed_default') {
    if (trimmed) {
      issues.push(
        adminIssue(
          'default_tare_not_allowed',
          'defaultTareLb',
          `${form.tareMode} tare profiles cannot store defaultTareLb.`,
          'core',
        ),
      );
    }
    return null;
  }

  if (!trimmed) {
    return null;
  }

  const parsed = parseNonNegativeNumber(trimmed);
  if (parsed == null || parsed > tareProfileSchemaLimits.maxTareLb) {
    issues.push(
      adminIssue(
        'invalid_default_tare',
        'defaultTareLb',
        `Default tare must be between 0 and ${String(tareProfileSchemaLimits.maxTareLb)}.`,
      ),
    );
    return null;
  }

  return parsed;
}

function parseNonNegativeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function addRequiredTextIssue(
  issues: AdminValidationIssue[],
  field: string,
  value: string,
  message: string,
): void {
  if (!value.trim()) {
    issues.push(adminIssue('required', field, message));
  }
}

function addTextLimitIssue(
  issues: AdminValidationIssue[],
  field: string,
  value: string,
  maxLength: number,
  message: string,
): void {
  if (value.trim().length > maxLength) {
    issues.push(adminIssue('field_limit_exceeded', field, message, 'contract'));
  }
}

function addDisplayOrderIssue(issues: AdminValidationIssue[], value: string): void {
  if (parseNonNegativeInteger(value) == null) {
    issues.push(
      adminIssue(
        'invalid_display_order',
        'displayOrder',
        'Display order must be a non-negative integer.',
      ),
    );
  }
}

function addUnitProfileValidationResult(
  issues: AdminValidationIssue[],
  result: UnitProfileValidationResult,
): void {
  for (const issue of result.issues) {
    issues.push(adminIssue(issue.code, issue.field, issue.message, 'core'));
  }
}

function addTareProfileValidationResult(
  issues: AdminValidationIssue[],
  result: TareProfileValidationResult,
): void {
  for (const issue of result.issues) {
    issues.push(adminIssue(issue.code, issue.field, issue.message, 'core'));
  }
}

function validatePayloadFields(
  payload: Record<string, unknown>,
  allowedFields: Set<string>,
  immutableFields: readonly string[],
): AdminValidationResult {
  const issues: AdminValidationIssue[] = [];

  for (const field of Object.keys(payload)) {
    if (!allowedFields.has(field)) {
      issues.push(
        adminIssue(
          immutableFields.includes(field)
            ? 'immutable_field_in_payload'
            : 'unknown_field_in_payload',
          field,
          `${field} is not allowed in this profile payload.`,
        ),
      );
    }
  }

  return toAdminValidationResult(issues);
}

function toAdminValidationResult(issues: AdminValidationIssue[]): AdminValidationResult {
  return {
    isValid: issues.every((issue) => issue.severity !== 'error'),
    issues,
    fieldErrors: buildFieldErrors(issues),
  };
}

function buildFieldErrors(issues: AdminValidationIssue[]): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  for (const issue of issues) {
    if (issue.severity !== 'error' || !issue.field) {
      continue;
    }
    errors[issue.field] = [...(errors[issue.field] ?? []), issue.message];
  }
  return errors;
}

function adminIssue(
  code: string,
  field: string | undefined,
  message: string,
  source: AdminValidationSource = 'admin',
  severity: AdminValidationSeverity = 'error',
): AdminValidationIssue {
  return {
    code,
    field,
    message,
    source,
    severity,
  };
}

function resolveSelectedTareProfile(
  form: UnitProfileAdminFormState,
  tareProfiles: TareProfileDto[] | null,
): TareProfileDto | null {
  if (!form.tareProfileId || !tareProfiles) {
    return null;
  }
  return tareProfiles.find((profile) => profile.id === form.tareProfileId) ?? null;
}

function deriveCapabilityPolicyForPreview(
  template: UnitProfileTemplateDefinition,
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null,
): HoldingUnitCapabilities {
  const validation = validateUnitProfileAdminCapabilityPolicy({
    templateKey: template.templateKey,
    capabilityPolicy,
  });
  if (validation.isValid) {
    return deriveUnitProfileCapabilityPolicy(template.templateKey, capabilityPolicy);
  }
  return {
    ...template.defaultCapabilityPolicy,
    ...(capabilityPolicy ?? {}),
  };
}

function lifecycleBooleans(
  capabilitiesByStatus: Record<HoldingUnitLifecycleStatus, HoldingUnitCapabilities>,
  capability: UnitProfileCapabilityName,
): Record<HoldingUnitLifecycleStatus, boolean> {
  const result = {} as Record<HoldingUnitLifecycleStatus, boolean>;
  for (const status of holdingUnitLifecycleStatusValues) {
    result[status] = capabilitiesByStatus[status][capability];
  }
  return result;
}

function buildWorkflowPreviewRow(input: {
  workflow: UnitProfileWorkflow;
  templateKey: string;
  form: UnitProfileAdminFormState;
  selectedTareProfile: TareProfileDto | null;
  includeInactive?: boolean;
}): UnitProfileWorkflowPreviewRow {
  const preview = previewUnitProfileWorkflowEligibility({
    templateKey: input.templateKey,
    workflow: input.workflow,
    capabilityPolicy: input.form.capabilityPolicy,
    tareProfile: input.selectedTareProfile
      ? {
          mode: input.selectedTareProfile.mode,
          defaultTareLb: input.selectedTareProfile.defaultTareLb,
        }
      : null,
    isActive: input.form.isActive,
    includeInactive: input.includeInactive,
  });
  return {
    workflow: input.workflow,
    ...getUnitProfileWorkflowLabel(input.workflow),
    isActive: preview.isActive,
    isEligible: preview.isEligible,
    validation: preview.validation,
  };
}

function getUnknownErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
